import { useState, useEffect } from 'react'
import { useParams, Link, useLocation } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { Menu } from '@headlessui/react'
import {
  ArrowLeftIcon,
  PrinterIcon,
  DocumentTextIcon,
  XCircleIcon,
  BanknotesIcon,
  DevicePhoneMobileIcon,
  CreditCardIcon,
  CheckCircleIcon,
  ArrowUturnLeftIcon,
  ChevronDownIcon,
  ReceiptRefundIcon,
  TruckIcon,
} from '@heroicons/react/24/outline'
import { saleService } from '../../services/saleService'
import { saleReversalService } from '../../services/saleReversalService'
import { invoiceService } from '../../services/invoiceService'
import { MpesaPaymentModal } from '../../components/payments/MpesaPaymentModal'
import { ReceiptModal } from '../../components/sales/ReceiptModal'
import { DeliveryNoteModal } from '../../components/sales/DeliveryNoteModal'
import { InvoiceModal } from '../../components/invoices/InvoiceModal'
import toast from 'react-hot-toast'

interface PaymentModalProps {
  isOpen: boolean
  onClose: () => void
  amount: number
  saleId: number
  customerId?: number
  onPaymentComplete: () => void
}

const PaymentMethodModal = ({ isOpen, onClose, amount, saleId, customerId, onPaymentComplete }: PaymentModalProps) => {
  const [selectedMethod, setSelectedMethod] = useState<string | null>(null)
  const [showMpesaModal, setShowMpesaModal] = useState(false)
  const [reference, setReference] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)

  if (!isOpen) return null

  const handleMethodSelect = (method: string) => {
    setSelectedMethod(method)
    if (method === 'mpesa') {
      setShowMpesaModal(true)
    }
  }

  const handleMpesaComplete = () => {
    setShowMpesaModal(false)
    onPaymentComplete()
    onClose()
  }

  const handleCashPayment = async () => {
    setIsProcessing(true)
    try {
      await saleService.addPayment(saleId, {
        amount,
        payment_method: selectedMethod,
        reference_number: reference || undefined,
      })
      toast.success('Payment received successfully')
      onPaymentComplete()
      onClose()
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to process payment')
    } finally {
      setIsProcessing(false)
    }
  }

  // Show payment confirmation for cash/card/cheque
  if (selectedMethod && selectedMethod !== 'mpesa') {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
          <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md w-full">
            <div className="bg-primary-600 px-4 py-4 sm:px-6">
              <h3 className="text-lg font-medium text-white">Confirm {selectedMethod.charAt(0).toUpperCase() + selectedMethod.slice(1)} Payment</h3>
              <p className="text-sm text-primary-100">Amount: KSh {amount.toLocaleString()}</p>
            </div>
            <div className="px-4 py-5 sm:p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reference Number (Optional)
                </label>
                <input
                  type="text"
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  placeholder={`Enter ${selectedMethod} reference...`}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setSelectedMethod(null)}
                  disabled={isProcessing}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Back
                </button>
                <button
                  onClick={handleCashPayment}
                  disabled={isProcessing}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isProcessing ? 'Processing...' : (
                    <>
                      <BanknotesIcon className="w-4 h-4" />
                      Confirm Payment
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
          <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md w-full">
            <div className="bg-primary-600 px-4 py-4 sm:px-6">
              <h3 className="text-lg font-medium text-white">Receive Payment</h3>
              <p className="text-sm text-primary-100">Amount: KSh {amount.toLocaleString()}</p>
            </div>
            <div className="px-4 py-5 sm:p-6">
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => handleMethodSelect('cash')}
                  className="p-4 border-2 border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors flex flex-col items-center gap-2"
                >
                  <BanknotesIcon className="w-8 h-8 text-green-600" />
                  <span className="font-medium">Cash</span>
                </button>
                <button
                  onClick={() => handleMethodSelect('mpesa')}
                  className="p-4 border-2 border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors flex flex-col items-center gap-2"
                >
                  <DevicePhoneMobileIcon className="w-8 h-8 text-green-600" />
                  <span className="font-medium">M-Pesa</span>
                </button>
                <button
                  onClick={() => handleMethodSelect('card')}
                  className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors flex flex-col items-center gap-2"
                >
                  <CreditCardIcon className="w-8 h-8 text-blue-600" />
                  <span className="font-medium">Card</span>
                </button>
                <button
                  onClick={() => handleMethodSelect('cheque')}
                  className="p-4 border-2 border-gray-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors flex flex-col items-center gap-2"
                >
                  <DocumentTextIcon className="w-8 h-8 text-purple-600" />
                  <span className="font-medium">Cheque</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <MpesaPaymentModal
        isOpen={showMpesaModal}
        onClose={() => setShowMpesaModal(false)}
        amount={amount}
        saleId={saleId}
        customerId={customerId}
        onPaymentComplete={handleMpesaComplete}
      />
    </>
  )
}

export const SaleDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [showReverseModal, setShowReverseModal] = useState(false)
  const [reverseReason, setReverseReason] = useState('')
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [showReceiptModal, setShowReceiptModal] = useState(false)
  const [showDeliveryModal, setShowDeliveryModal] = useState(false)
  const [showInvoiceModal, setShowInvoiceModal] = useState(false)
  const [invoiceData, setInvoiceData] = useState<any | null>(null)
  const [loadingInvoice, setLoadingInvoice] = useState(false)

  // Auto-open payment modal if navigated from POS with openPaymentModal state
  useEffect(() => {
    if (location.state?.openPaymentModal) {
      setShowPaymentModal(true)
      // Clear the state so it doesn't reopen on refresh
      window.history.replaceState({}, document.title)
    }
  }, [location.state])

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['sale', id],
    queryFn: () => saleService.getById(Number(id)),
  })

  const cancelMutation = useMutation({
    mutationFn: () => saleService.cancel(Number(id)),
    onSuccess: () => {
      toast.success('Sale cancelled successfully')
      setShowCancelModal(false)
      refetch()
    },
    onError: () => {
      toast.error('Failed to cancel sale')
    },
  })

  const reverseMutation = useMutation({
    mutationFn: () => saleReversalService.reverseSale({ sale_id: Number(id), reason: reverseReason }),
    onSuccess: () => {
      toast.success('Sale reversed successfully')
      setShowReverseModal(false)
      setReverseReason('')
      refetch()
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to reverse sale')
    },
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  const sale = data?.data?.data
  if (!sale) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Sale not found</p>
        <Link to="/sales" className="text-primary-600 hover:underline mt-2 inline-block">
          Back to Sales
        </Link>
      </div>
    )
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800'
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'cancelled': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800'
      case 'partially_paid': return 'bg-yellow-100 text-yellow-800'
      case 'partial': return 'bg-yellow-100 text-yellow-800'
      case 'unpaid': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const subtotal = Number(sale.subtotal || 0)
  const tax = Number(sale.tax_amount || 0)
  const discount = Number(sale.discount_amount || 0)
  const total = Number(sale.total_amount || 0)
  const balance = Number(sale.balance_due || 0)
  const amountPaid = Number(sale.amount_paid || 0)

  const handlePaymentComplete = () => {
    refetch()
    toast.success('Payment received successfully')
  }

  const isWalkIn = !sale.customers || sale.customers.customer_code === 'WALKIN'

  const handleViewInvoice = async () => {
    setLoadingInvoice(true)
    try {
      const response = await invoiceService.getBySale(sale.sale_id)
      setInvoiceData(response.data?.data)
      setShowInvoiceModal(true)
    } catch {
      if (isWalkIn) {
        toast.error('Invoices are available for Invoice/Credit clients. Post a receipt to get the Invoice.')
      } else {
        toast.error('No invoice found for this sale')
      }
    } finally {
      setLoadingInvoice(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Payment Modal */}
      {balance > 0 && (
        <PaymentMethodModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          amount={balance}
          saleId={sale.sale_id}
          customerId={sale.customer_id}
          onPaymentComplete={handlePaymentComplete}
        />
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link
            to="/sales"
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{sale.sale_number}</h1>
            <p className="text-sm text-gray-500">
              {new Date(sale.sale_date).toLocaleString()}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Menu as="div" className="relative">
            <Menu.Button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
              <PrinterIcon className="w-4 h-4" />
              Print
              <ChevronDownIcon className="w-3.5 h-3.5" />
            </Menu.Button>
            <Menu.Items className="absolute left-0 z-10 mt-1 w-56 origin-top-left rounded-lg bg-white shadow-lg ring-1 ring-black/5 focus:outline-none">
              <div className="py-1">
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={() => setShowReceiptModal(true)}
                      className={`flex items-center gap-2 w-full px-4 py-2 text-sm text-left ${
                        active ? 'bg-gray-50 text-gray-900' : 'text-gray-700'
                      }`}
                    >
                      <ReceiptRefundIcon className="w-4 h-4 text-gray-400" />
                      Print Receipt
                    </button>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={() => setShowDeliveryModal(true)}
                      className={`flex items-center gap-2 w-full px-4 py-2 text-sm text-left ${
                        active ? 'bg-gray-50 text-gray-900' : 'text-gray-700'
                      }`}
                    >
                      <TruckIcon className="w-4 h-4 text-gray-400" />
                      Print Delivery Note
                    </button>
                  )}
                </Menu.Item>
              </div>
            </Menu.Items>
          </Menu>
          {sale.status !== 'cancelled' && (
            <>
              <button
                onClick={handleViewInvoice}
                disabled={loadingInvoice}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                <DocumentTextIcon className="w-4 h-4" />
                {loadingInvoice ? 'Loading...' : 'Invoice'}
              </button>
              {balance > 0 && sale.status !== 'paid' && (
                <button
                  onClick={() => setShowPaymentModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  <BanknotesIcon className="w-4 h-4" />
                  Receive Payment
                </button>
              )}
              {balance === 0 && (
                <span className="flex items-center gap-2 px-4 py-2 bg-green-100 text-green-800 rounded-lg">
                  <CheckCircleIcon className="w-4 h-4" />
                  Paid
                </span>
              )}
              <button
                onClick={() => setShowCancelModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                <XCircleIcon className="w-4 h-4" />
                Cancel
              </button>
              <button
                onClick={() => setShowReverseModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-rose-700 text-white rounded-lg hover:bg-rose-800"
              >
                <ArrowUturnLeftIcon className="w-4 h-4" />
                Reverse
              </button>
            </>
          )}
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <p className="text-sm text-gray-500">Sale Status</p>
          <span className={`inline-flex px-3 py-1 mt-2 text-sm font-medium rounded-full ${getStatusColor(sale.status)}`}>
            {sale.status}
          </span>
        </div>
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <p className="text-sm text-gray-500">Payment Status</p>
          <span className={`inline-flex px-3 py-1 mt-2 text-sm font-medium rounded-full ${getPaymentStatusColor(sale.payment_status)}`}>
            {sale.payment_status}
          </span>
        </div>
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <p className="text-sm text-gray-500">Total Amount</p>
          <p className="text-xl font-bold text-gray-900 mt-1">
            KSh {total.toLocaleString()}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <p className="text-sm text-gray-500">Balance Due</p>
          <p className={`text-xl font-bold mt-1 ${balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
            KSh {balance.toLocaleString()}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sale Items */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="font-semibold text-gray-900">Sale Items</h2>
            </div>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Qty</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Price</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sale.sale_items?.map((item: any) => (
                  <tr key={item.item_id}>
                    <td className="px-6 py-4">
                      <p className="font-medium text-gray-900">
                        {item.products?.product_name}
                        {item.is_bundle && <span className="ml-2 text-xs text-primary-600">(Bundle)</span>}
                      </p>
                      <p className="text-sm text-gray-500">{item.products?.sku}</p>
                      {item.sale_item_serials?.length > 0 && (
                        <p className="text-xs text-gray-400">
                          Serials: {item.sale_item_serials.map((s: any) => s.serial_numbers?.serial_number).join(', ')}
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">{item.quantity}</td>
                    <td className="px-6 py-4 text-right">KSh {Number(item.unit_price).toLocaleString()}</td>
                    <td className="px-6 py-4 text-right font-medium">KSh {Number(item.total_price).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Payments */}
          <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="font-semibold text-gray-900">Payment History</h2>
              {amountPaid > 0 && (
                <span className="text-sm text-gray-500">
                  Total Paid: <span className="font-medium text-green-600">KSh {amountPaid.toLocaleString()}</span>
                </span>
              )}
            </div>
            {sale.payments?.length > 0 ? (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {sale.payments.map((payment: any) => (
                    <tr key={payment.payment_id}>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {new Date(payment.payment_date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {payment.payment_methods?.method_name?.toLowerCase().includes('mpesa') && (
                            <DevicePhoneMobileIcon className="w-4 h-4 text-green-600" />
                          )}
                          {payment.payment_methods?.method_name?.toLowerCase().includes('cash') && (
                            <BanknotesIcon className="w-4 h-4 text-green-600" />
                          )}
                          {payment.payment_methods?.method_name?.toLowerCase().includes('card') && (
                            <CreditCardIcon className="w-4 h-4 text-blue-600" />
                          )}
                          <span className="text-sm text-gray-600 capitalize">
                            {payment.payment_methods?.method_name?.replace('_', ' ')}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 font-mono">
                        {payment.reference_number || payment.transaction_reference || '-'}
                      </td>
                      <td className="px-6 py-4 text-right font-medium">
                        KSh {Number(payment.amount).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-8 text-center text-gray-500">
                <BanknotesIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No payments received yet</p>
                {balance > 0 && (
                  <p className="text-sm mt-1">Click "Receive Payment" to record a payment</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Payment Actions */}
          {balance > 0 ? (
            <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white">
              <h2 className="font-semibold mb-2">Balance Due</h2>
              <p className="text-3xl font-bold mb-4">KSh {balance.toLocaleString()}</p>
              <button
                onClick={() => setShowPaymentModal(true)}
                className="w-full py-3 bg-white text-green-600 rounded-lg font-medium hover:bg-green-50 transition-colors flex items-center justify-center gap-2"
              >
                <BanknotesIcon className="w-5 h-5" />
                Receive Payment
              </button>
              <p className="text-xs text-green-100 mt-3 text-center">
                Accept Cash, M-Pesa, Card, or Cheque
              </p>
            </div>
          ) : (
            <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-full">
                  <CheckCircleIcon className="w-8 h-8 text-green-600" />
                </div>
                <div>
                  <h2 className="font-semibold text-green-800 text-lg">Fully Paid</h2>
                  <p className="text-green-600">KSh {amountPaid.toLocaleString()} received</p>
                </div>
              </div>
              {sale.payments?.length > 0 && (
                <p className="text-sm text-green-700 mt-3">
                  {sale.payments.length} payment{sale.payments.length > 1 ? 's' : ''} recorded
                </p>
              )}
            </div>
          )}

          {/* Customer Info */}
          <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
            <h2 className="font-semibold text-gray-900 mb-4">Customer</h2>
            {sale.customers ? (
              <div>
                <p className="font-medium text-gray-900">
                  {sale.customers.company_name || `${sale.customers.first_name} ${sale.customers.last_name}`}
                </p>
                {sale.customers.email && (
                  <p className="text-sm text-gray-500">{sale.customers.email}</p>
                )}
                {sale.customers.phone && (
                  <p className="text-sm text-gray-500">{sale.customers.phone}</p>
                )}
                <p className="text-sm text-primary-600 mt-2">
                  {sale.sale_type === 'retail' ? 'Retail Price' : 
                   sale.sale_type === 'wholesale' ? 'Wholesale Price' : 
                   'Special Price'}
                </p>
              </div>
            ) : (
              <p className="text-gray-500">Walk-in Customer</p>
            )}
          </div>

          {/* Summary */}
          <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
            <h2 className="font-semibold text-gray-900 mb-4">Summary</h2>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subtotal</span>
                <span>KSh {subtotal.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">
                  {sale.vat_type === 'vat_exclusive' ? `VAT Excl. (${sale.tax_percent ?? 0}%)` :
                   sale.vat_type === 'vat_inclusive' ? `VAT Incl. (${sale.tax_percent ?? 0}%)` : 'VAT'}
                </span>
                <span>KSh {tax.toLocaleString()}</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Discount</span>
                  <span>- KSh {discount.toLocaleString()}</span>
                </div>
              )}
              <div className="border-t pt-2 mt-2">
                <div className="flex justify-between font-bold text-lg">
                  <span>Total</span>
                  <span>KSh {total.toLocaleString()}</span>
                </div>
              </div>
              {amountPaid > 0 && (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Amount Paid</span>
                    <span className="text-green-600">KSh {amountPaid.toLocaleString()}</span>
                  </div>
                  {balance > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Balance</span>
                      <span className="text-red-600 font-medium">KSh {balance.toLocaleString()}</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Sale Info */}
          <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
            <h2 className="font-semibold text-gray-900 mb-4">Sale Information</h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Created By</span>
                <span className="text-gray-900">{sale.users?.first_name} {sale.users?.last_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Sale Date</span>
                <span className="text-gray-900">{new Date(sale.sale_date).toLocaleDateString()}</span>
              </div>
              {sale.invoices?.[0] && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Invoice</span>
                  <Link
                    to={`/invoices/${sale.invoices[0].invoice_id}`}
                    className="text-primary-600 hover:underline"
                  >
                    {sale.invoices[0].invoice_number}
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Terms & Conditions */}
          {sale.terms_conditions && (
            <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-2">Terms & Conditions</h3>
              <p className="text-sm text-gray-600 whitespace-pre-line">{sale.terms_conditions}</p>
            </div>
          )}
        </div>
      </div>

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Cancel Sale</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to cancel this sale? This will restore inventory and cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowCancelModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                No, Keep Sale
              </button>
              <button
                onClick={() => cancelMutation.mutate()}
                disabled={cancelMutation.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {cancelMutation.isPending ? 'Cancelling...' : 'Yes, Cancel Sale'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reverse Modal */}
      {showReverseModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Reverse Sale</h3>
            <p className="text-gray-600 mb-4">
              This will reverse the sale, restore all stock, cancel the invoice, and reverse payments. Please provide a reason.
            </p>
            <textarea
              value={reverseReason}
              onChange={(e) => setReverseReason(e.target.value)}
              placeholder="Reason for reversal..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 mb-4"
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowReverseModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => reverseMutation.mutate()}
                disabled={reverseMutation.isPending || !reverseReason.trim()}
                className="px-4 py-2 bg-rose-700 text-white rounded-lg hover:bg-rose-800 disabled:opacity-50"
              >
                {reverseMutation.isPending ? 'Reversing...' : 'Reverse Sale'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Receipt Modal */}
      <ReceiptModal
        isOpen={showReceiptModal}
        onClose={() => setShowReceiptModal(false)}
        sale={sale}
      />

      {/* Delivery Note Modal */}
      <DeliveryNoteModal
        isOpen={showDeliveryModal}
        onClose={() => setShowDeliveryModal(false)}
        sale={sale}
      />

      {/* Invoice Modal */}
      {showInvoiceModal && invoiceData && (
        <InvoiceModal
          isOpen={showInvoiceModal}
          onClose={() => {
            setShowInvoiceModal(false)
            setInvoiceData(null)
          }}
          invoice={invoiceData}
        />
      )}
    </div>
  )
}
